import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelSelect, { GenericSelectOption } from '../../../components/voxel/Forms/VoxelSelect';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { TeamMemberGq } from '../../../graphql/users/models/CurrentTeamUserGq';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import {
  UserTeamRoleGq,
  userCanEditMemberWithRole,
} from '../../../graphql/users/models/UserTeamRoleGq';
import { usersApiService } from '../../../graphql/users/usersApiService';

export interface EditTeamRoleDialogProps {
  editTeamMember: TeamMemberGq;
  onSuccess: (Success: boolean) => Promise<any>;
  close: () => void;
  currentUser: CurrentUserGq;
  ownerCount: number;
}

interface FormState {
  role: UserTeamRoleGq;
}

export const EditTeamRoleDialog: React.FC<EditTeamRoleDialogProps> = ({
  editTeamMember,
  onSuccess,
  close,
  currentUser,
  ownerCount,
}) => {
  const updateTeamRoleMutation = usersApiService.mutations.useUpdateTeamRole();
  const formikProps = useFormik<FormState>({
    initialValues: {
      role: editTeamMember.teamRole,
    },
    enableReinitialize: true,
    validate: (values) => {
      if (
        editTeamMember.teamRole === UserTeamRoleGq.OWNER &&
        ownerCount < 2 &&
        values.role !== UserTeamRoleGq.OWNER
      ) {
        return {
          role: 'Every workspace needs to have at least one owner. Transfer the owner role to someone else before continuing.',
        };
      }
      return {};
    },
    onSubmit: async (values, actions) =>
      updateTeamRoleMutation({
        memberId: editTeamMember.id,
        newRole: values.role,
      })
        .catch((error) => console.error(error))
        .then(onSuccess)
        .finally(() => close()),
  });

  // Only allow these options for higher roles (so that you cannot invite over your own role)
  var memberRoleList: GenericSelectOption[] = useMemo(
    () => [
      {
        value: UserTeamRoleGq.MEMBER,
        title: 'Member',
        subtitle: 'Basic access',
        disabled: false,
      },
      {
        value: UserTeamRoleGq.ADMIN,
        title: 'Admin',
        subtitle: 'May edit basic settings, invite new users and set up team connections',
        disabled: !userCanEditMemberWithRole(currentUser, UserTeamRoleGq.ADMIN),
      },
      {
        value: UserTeamRoleGq.OWNER,
        title: 'Owner',
        subtitle: 'May edit subscription and billing in addition to admin rights',
        disabled: !userCanEditMemberWithRole(currentUser, UserTeamRoleGq.OWNER),
      },
    ],
    [currentUser]
  );

  return (
    <VoxelDialog
      title="Edit role"
      open={true}
      onClose={close}
      closeDisabled={formikProps.isSubmitting}
      maxWClass="max-w-2xl"
    >
      <VoxelDialogContent className=" flex flex-col gap-4">
        <div className="flex flex-row items-center gap-4 grow">
          <img src={editTeamMember?.picture} className="rounded-full h-7" />
          <div>
            <VoxelText variant="title3" color="dark" noWrap>
              {editTeamMember?.givenName + ' ' + editTeamMember?.familyName}
            </VoxelText>
            <VoxelText color="light" noWrap>
              {editTeamMember?.email}
            </VoxelText>
          </div>
        </div>
        <VoxelSelect
          label="Role"
          labelPosition="top"
          options={memberRoleList}
          selectedOptionValue={formikProps.values.role}
          setSelectedOption={(opt: GenericSelectOption) =>
            formikProps.setFieldValue('role', opt.value)
          }
          disabled={formikProps.isSubmitting}
          helperText={formikProps.errors?.role}
          showHelperText={!!formikProps.errors?.role?.length && formikProps.dirty}
          inputWidthClass="w-96"
        />
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionCancel />
        <VoxelDialogActionSubmit
          title="Change role"
          onClick={formikProps.submitForm}
          disabled={formikProps.isSubmitting || !formikProps.dirty || !formikProps.isValid}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default EditTeamRoleDialog;
